import React, { ReactNode, useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import delve from 'dlv';

import useSearchOnItems, { TFindItemMethod } from 'hooks/useSearchOnItems';

import MenuRoot from './index';
import SearchField, {
  SearchFieldContainer,
} from 'components/styled/SearchField/SearchField';
import { ItemWrapper } from './Menu';
import SelectedAndRecentField from '../TopicFiltersDropdown/GenreDropdown/SelectedAndRecentField';

import { boxShadow, helperText } from 'styles/base-classes';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import { ThemeProps } from 'types/styled';
import useListKeyboardNavigation from './useKeyboardNavigation';
import { primaryTypography } from 'styles/global/typography/typography';
import sortByDisplayName from 'helpers/sortByDisplayName';

export const LayoutWrapper = styled.div<ThemeProps<'div'>>`
  ${boxShadow};
  position: absolute;
  z-index: 9999;
  right: ${(p: any): string | undefined => p.overflowLeft && '0'};
  background: white;
  @media screen and (max-width: 47em) {
    width: 100%;
  }

  ${SearchFieldContainer} {
    margin: ${({ theme }): string =>
      `${theme.spacing.small} ${theme.spacing.large}`};
  }
`;

export const ListsScrollableWrapper = styled.ul<any>`
  overflow-y: auto;
  max-height: ${({ maxHeight }): string => (maxHeight ? maxHeight : '20em')};
  overscroll-behavior-y: contain;
`;

export const Figure = styled.figure`
  margin: 0;
  text-align: left;
  min-width: 100%;
`;

export const Line = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${colors.LightGrey};
`;

export const HelperText = styled.p`
  ${helperText};
  font-weight: normal;
  padding: 0 1em;
`;

export const DisabledMessage = styled.p`
  ${helperText};
  ${primaryTypography}
  font-size: 1em;
  min-width: 13em;
  padding: 0 4px;
  @media screen and (max-width: 48em) {
    padding: 0 4em;
  }
`;

export type TItemToString = (item: any) => string | null;
type TGetProps = (item: any) => any;

export interface ISearchableMenuProps {
  items: any[];
  selectedItem?: any;
  selectedItems?: any;
  multipleSelections?: any;
  Item: any;
  ListLabel: any;
  itemToString: TItemToString;
  getMenuProps: TGetProps;
  getItemProps: TGetProps;
  overflowLeft: boolean;
  entityColorScale: Function;
  additionalFindMethod: TFindItemMethod;
  children?: ReactNode;
  highlightedIndex: number;
  setHighlightedIndex: Function;
  selectHighlightedItem: Function;
  isDisplayedWithFlag?: boolean;
  showOnlyRecent: boolean;
  showListLabel: boolean;
  disabledMessage?: string;
  disabled: boolean;
  hideRecentBlock?: boolean;
  showAllItems?: boolean;
  isSmallScreen?: boolean;
  isMultipleSelection?: boolean;
  textToSelect?: string;
  maxHeight?: string;
}

/**
 * The searchable dropdown composes `MenuRoot` (Menu/index.js)
 * with the unique logic needed to make the dropdown searchable,
 * as well as supporting multiple lists. Multiple lists are derived
 * from the `children` key and lists can have a label too (`{label: 'foo', children []}`).
 */
const SearchableMenu: React.FC<ISearchableMenuProps> = (props) => {
  const {
    items,
    selectedItem,
    selectedItems,
    multipleSelections,
    Item,
    ListLabel,
    itemToString,
    getMenuProps,
    getItemProps,
    overflowLeft,
    entityColorScale,
    additionalFindMethod,
    children,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem,
    isDisplayedWithFlag,
    hideRecentBlock = false,
    showOnlyRecent = false,
    showListLabel = false,
    showAllItems = true,
    disabledMessage,
    disabled,
    isSmallScreen,
    isMultipleSelection,
    textToSelect,
    maxHeight,
  } = props;

  const [searchValue, setSearchValue] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);

  const itemList = useMemo(() => {
    const itemsToSort = multipleSelections
      ? [...multipleSelections, ...items]
      : items;
    return showOnlyRecent && (!searchValue || searchValue === '')
      ? itemsToSort?.filter((i: any) => i?.isRecent)
      : itemsToSort;
  }, [items, showOnlyRecent, searchValue, multipleSelections]);

  let recentItems = [];
  let itemsWithoutRecent = [] as any[];
  if (isMultipleSelection && multipleSelections?.length < 3 && !searchValue) {
    recentItems = items.filter((i: any) => i.isRecent);
    recentItems.splice(3);
    recentItems.sort(sortByDisplayName);
    itemsWithoutRecent = items.slice(recentItems.length);
  }

  const filteredItems = useSearchOnItems(
    itemList,
    searchValue,
    additionalFindMethod
  );
  const isLengthZero = (item: any): boolean =>
    delve(item, 'children', item).length === 0;
  const noResults = filteredItems.every(isLengthZero);

  useListKeyboardNavigation(
    filteredItems.length,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem
  );

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleSearch = (evt: any): void => {
    setHighlightedIndex(0);
    setSearchValue(evt.target.value);
  };

  const itemsToShow = useMemo(() => {
    let res;
    if (!isMultipleSelection || searchValue) {
      return filteredItems;
    }
    if (recentItems?.length && itemsWithoutRecent) {
      res = itemsWithoutRecent;
    } else {
      res = items;
    }
    // sort because we could have a few recent genres at the top
    return res.sort(sortByDisplayName);
  }, [
    recentItems,
    itemsWithoutRecent,
    items,
    searchValue,
    filteredItems,
    isMultipleSelection,
  ]);

  return (
    <LayoutWrapper overflowLeft={overflowLeft}>
      {!disabled ? (
        <>
          <Box borderBottom={`1px solid ${colors.LightGrey}`}>
            <SearchField
              refObj={searchRef}
              handleSearch={handleSearch}
              value={searchValue}
              placeholder="Search"
            />
          </Box>

          {!noResults && (
            <ListsScrollableWrapper
              {...getMenuProps({ 'data-menu-list': true })}
              maxHeight={maxHeight}
            >
              {ListLabel && showListLabel && (
                <li>
                  <Figure data-menu-figure={true}>
                    {ListLabel && filteredItems.length > 0 && (
                      <figcaption>
                        <ListLabel {...props} />
                      </figcaption>
                    )}
                  </Figure>
                </li>
              )}

              {isMultipleSelection && !hideRecentBlock && (
                <SelectedAndRecentField
                  searchValue={searchValue}
                  multipleSelections={multipleSelections}
                  ListLabel={ListLabel}
                  Item={Item}
                  highlightedIndex={highlightedIndex}
                  entityColorScale={entityColorScale}
                  isDisplayedWithFlag={isDisplayedWithFlag}
                  isSmallScreen={isSmallScreen}
                  itemToString={itemToString}
                  getItemProps={getItemProps}
                  recentItems={recentItems}
                  selectedItem={selectedItem}
                  selectedItems={selectedItems}
                  lengthOfAllItems={filteredItems.length}
                  showAllItems={showAllItems && itemsToShow.length > 0}
                  textToSelect={textToSelect}
                />
              )}

              {(showAllItems || searchValue) &&
                itemsToShow.map((item, index) => {
                  const isSubList = item && 'children' in item;

                  return isSubList ? (
                    // when a sublist is present, we construct a new list
                    // within a list item.
                    <li key={`root-list-item-${item.label}`}>
                      <Figure data-menu-figure={true}>
                        {ListLabel && item.children.length > 0 && (
                          <figcaption>
                            <ListLabel label={item.label} />
                          </figcaption>
                        )}
                        <ul>
                          {item.children.map(
                            (child: any, index: number): ReactNode => (
                              <ItemWrapper
                                key={`sub-list-${itemToString(child)}`}
                                {...getItemProps({
                                  'data-menu-list-item': true,
                                  key: itemToString(child),
                                  item: child,
                                })}
                              >
                                <Item
                                  item={child}
                                  index={index}
                                  isHovered={index === highlightedIndex}
                                  isActive={
                                    itemToString(selectedItem) ===
                                    itemToString(child)
                                  }
                                  entityColorScale={entityColorScale}
                                  isSmallScreen={isSmallScreen}
                                />
                              </ItemWrapper>
                            )
                          )}
                        </ul>
                      </Figure>
                    </li>
                  ) : (
                    // otherwise we can just render a list item.
                    item && (
                      <ItemWrapper
                        key={itemToString(item)}
                        {...getItemProps({
                          'data-menu-list-item': true,
                          key: itemToString(item),
                          item: item,
                          isActive: index === highlightedIndex,
                        })}
                      >
                        <Item
                          item={item}
                          index={index}
                          isHovered={index === highlightedIndex}
                          isActive={
                            isMultipleSelection
                              ? selectedItems &&
                                selectedItems.includes(itemToString(item))
                              : itemToString(selectedItem) ===
                                itemToString(item)
                          }
                          entityColorScale={entityColorScale}
                          isDisplayedWithFlag={isDisplayedWithFlag}
                          isMultipleSelection={isMultipleSelection}
                          isSmallScreen={isSmallScreen}
                        />
                      </ItemWrapper>
                    )
                  );
                })}
            </ListsScrollableWrapper>
          )}

          {noResults && (
            <HelperText>
              Didn&apos;t find what you&apos;re looking for? <br /> Try again
              with a different search term or try a different spelling.
            </HelperText>
          )}

          {children}
        </>
      ) : (
        disabledMessage && <DisabledMessage>{disabledMessage}</DisabledMessage>
      )}
    </LayoutWrapper>
  );
};

const Menu: React.FC<any> = (props) => {
  return (
    <MenuRoot {...props}>
      {(renderProps): any => (
        <SearchableMenu {...props} {...renderProps}>
          {props.children}
        </SearchableMenu>
      )}
    </MenuRoot>
  );
};

export default Menu;
